import type {AutocompleteItem} from '@mantine/core';
import {useRouter} from 'next/router';
import {useEffect, useState} from 'react';
import {ReportCategoryTypes} from '../../../core/entities/reports/constants';
import {convertReportCategoryTypeIdsToStrings} from '../../../core/factories/reports/convertReportCategoryTypeIdsToStrings';
import {flattenCompanyListByCategory} from '../../../core/factories/reports/flattenCompanyListByCategory';
import {
  useDepartmentMaster,
  useIndustryCategories,
  useReportsCount,
} from '../../../core/repositories/reportsRepository';
import {getReportUrlForPc} from './getReportUrlForPc';

export type FilterItem = {
  reportType: number[];
  industry: number[];
  department: number[];
};

export type UseHandleReportsReturnType = ReturnType<typeof useHandleReports>;

export const useHandleReports = () => {
  const reportTypes = ReportCategoryTypes;
  const {data: industryCategories, isLoading: isLoadingIndustryCategories} =
    useIndustryCategories({});
  const {data: departmentMaster, isLoading: isLoadingDepartmentMaster} =
    useDepartmentMaster({
      queryParams: {
        isPopular: 'true',
      },
    });

  const [filterItems, setFilterItems] = useState<FilterItem>({
    reportType: [],
    industry: [],
    department: [],
  });

  const router = useRouter();
  const {type, category, department, q} = router.query;
  // 明示的に文字列型として扱う
  const typeStr = type as string;
  const categoryStr = category as string;
  const departmentStr = department as string;
  const qStr = q as string;

  const [searchWord, setSearchWord] = useState(qStr ?? '');

  useEffect(() => {
    if (!industryCategories?.ok || !departmentMaster?.ok) {
      return;
    }

    const reportTypeFilterItem = typeStr
      ? typeStr
          .split(',')
          .flatMap((t) =>
            ReportCategoryTypes.types
              .filter((reportType) => reportType.key === t)
              .map((reportType) => reportType.id),
          )
      : [];
    const newFilterItems: FilterItem = {
      reportType: reportTypeFilterItem,
      industry: categoryStr
        ? categoryStr.split(',').map((id) => Number.parseInt(id, 10))
        : [],
      department: departmentStr
        ? departmentStr.split(',').map((id) => Number.parseInt(id, 10))
        : [],
    };
    setFilterItems(newFilterItems);
  }, [industryCategories, departmentMaster]);

  // 動的にURLを変更する
  useEffect(() => {
    const url: string = getReportUrlForPc(filterItems);
    router.replace(url).catch((error) => {
      console.error('Failed to push the new URL', error);
    });
  }, [filterItems]);

  const {data: reportsCount, isLoading: isLoadingReportsCount} =
    useReportsCount({
      queryParams: {
        types: convertReportCategoryTypeIdsToStrings(filterItems.reportType),
        industryCategoryIDs: filterItems.industry,
        departmentMasterIDs: filterItems.department,
      },
    });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [reportsCount]);

  const handleSearchInputChange = (searchWord: string) => {
    setSearchWord(searchWord);
  };

  const autoCompleteData: AutocompleteItem[] = reportsCount?.ok
    ? flattenCompanyListByCategory(reportsCount.value).map((company) => ({
        id: company.companyID,
        value: company.companyName,
      }))
    : [];

  const isLoading =
    isLoadingIndustryCategories ||
    isLoadingDepartmentMaster ||
    isLoadingReportsCount;

  return {
    industryCategories,
    departmentMaster,
    reportTypes,
    searchWord,
    setSearchWord,
    filterItems,
    setFilterItems,
    reportsCount,
    autoCompleteData,
    handleSearchInputChange,
    isLoading,
  };
};
